import React, {
  lazy,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
} from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import ClearIcon from "@mui/icons-material/Clear";
import HistoryIcon from "@mui/icons-material/History";
import "../../Components/SharedComponents/InputBox.scss";
import {
  MaterialReactTable,
  // createRow,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  type MRT_Row,
  type MRT_TableOptions,
  useMaterialReactTable,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from "material-react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { type User } from "./makeData";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "react-bootstrap/Accordion";
import { Container, Row } from "reactstrap";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import {
  showDeleteConfirmModal,
  ResetTablePreferencesButton,
} from "../../Components/ConfirmModal";
import { CompactTextField } from "../../Components/SharedComponents/SharedComponents";
import FileUpload from "../../Components/FileUpload/FileUpload";
import BrokerTemplateDownloadButton from "../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
import { useNavigate } from "react-router-dom";
import { useTablePersistence } from "../../hooks/useTablePersistence";
import { TableActionVisibilityMenu } from "../../Components/SharedComponents/TableActionVisibilityMenu";
import { SortableActionButton } from "../../Components/SharedComponents/SortableActionButton";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";

const Example = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const {
    state: tableState,
    resetState: resetTableState,
    setColumnFilters,
    setColumnVisibility,
    setColumnOrder,
    setColumnPinning,
    setColumnSizing,
    setDensity,
    setGlobalFilter,
    setShowColumnFilters,
    setShowGlobalFilter,
    setSorting,
    setPagination,
    setGrouping,
    setActionButtonOrder,
    setActionButtonVisibility,
    setIsFullScreen,
  } = useTablePersistence("broker_table", {
    density: "comfortable",
    pagination: { pageIndex: 0, pageSize: 100 },
    columnFilters: [],
    columnVisibility: {},
    columnOrder: [],
    columnPinning: {},
    columnSizing: {},
    sorting: [],
    grouping: [],
    globalFilter: "",
    showColumnFilters: true,
    showGlobalFilter: true,
    actionButtonOrder: [],
    actionButtonVisibility: {},
    isFullScreen: false,
  });

  const columnFilters = tableState.columnFilters || [];
  const columnVisibility = tableState.columnVisibility || {};
  const density = tableState.density || "comfortable";
  const globalFilter = tableState.globalFilter;
  const showGlobalFilter = tableState.showGlobalFilter ?? true;
  const showColumnFilters = tableState.showColumnFilters ?? true;
  const sorting = tableState.sorting || [];
  const pagination = tableState.pagination || { pageIndex: 0, pageSize: 100 };
  const columnOrder = tableState.columnOrder || [];
  const columnPinning = tableState.columnPinning || {};
  const columnSizing = tableState.columnSizing || {};
  const grouping = tableState.grouping || [];
  const actionButtonOrder = tableState.actionButtonOrder || [];
  const actionButtonVisibility = tableState.actionButtonVisibility || {};
  const isFullScreen = tableState.isFullScreen ?? false;

  const defaultButtonOrder = [
    "upload",
    "export_all",
    "history",
    "template",
    "export_selected",
    // "export_all_rows",
    // "export_page_rows",
    "delete_selected",
    "reset"
  ];

  const buttonsConfig: Record<string, { label: string, tooltipText: string, description: string }> = {
    "upload": { label: "Upload File", tooltipText: "Upload new template • Drag to reorder", description: "Upload new template" },
    "export_all": { label: "Export All Data", tooltipText: "Export all records • Drag to reorder", description: "Export all records" },
    "history": { label: "History Log", tooltipText: "View activity history • Drag to reorder", description: "View activity history" },
    "template": { label: "Download Template", tooltipText: "Download empty template • Drag to reorder", description: "Download empty template" },
    "export_selected": { label: "Export Selected Rows", tooltipText: "Export selected • Drag to reorder", description: "Export selected" },
    // "export_all_rows": { label: "Export All Rows", tooltipText: "Export all rows (ignores pagination) • Drag to reorder", description: "Export all rows including next page" },
    // "export_page_rows": { label: "Export Page Rows", tooltipText: "Export current page rows • Drag to reorder", description: "Export rows as seen on screen" },
    "delete_selected": { label: "Delete Selected", tooltipText: "Delete selected • Drag to reorder", description: "Delete selected" },
    "reset": { label: "Reset View", tooltipText: "Reset table preferences • Drag to reorder", description: "Reset table preferences" }
  };

  const currentActionButtonOrder = actionButtonOrder.length > 0 ? actionButtonOrder : defaultButtonOrder;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = currentActionButtonOrder.indexOf(active.id as string);
      const newIndex = currentActionButtonOrder.indexOf(over.id as string);
      const newOrder = arrayMove(currentActionButtonOrder, oldIndex, newIndex);
      setActionButtonOrder(newOrder);
    }
  };

  const [filterModes, setFilterModes] = useState<
    { mode: string; id: string; value: unknown }[]
  >([]);

  const [brokerName, setBrokerName] = useState<string>("");
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");
  const [showRowResizing, setShowRowResizing] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState(0);

  const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);

  const handleFileUploadSubmit = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file); // The key should match your API expectation

    try {
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/upload/broker/`, // Update endpoint
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Refresh table data after successful upload
      queryClient.invalidateQueries({ queryKey: ["users"] });

      return {
        success: true,
        message: "File uploaded and data added successfully!",
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to upload file",
      };
    }
  };

  const navigate = useNavigate();
  const handlehistory = () => {
    navigate("/settings/log/history/broker");
  };

  const openrowhistory = (row: any) => {
    navigate(`/settings/log/history/broker/${row.id}`);
  };
  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      // {
      //   accessorKey: "id",
      //   header: "Id",
      //   enableEditing: false,

      //   // minSize: 40,
      //   // maxSize: 100,
      // },

      {
        accessorKey: "name",
        header: "Broker Name",
        enableEditing: true,

        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => {
            table.setEditingRow(row);
          },
          sx: {
            height: "10px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(44, 4, 4, 0.05)",
            },
            ...(table.getState().editingRow?.id === row.id && {
              backgroundColor: "rgba(0, 128, 0, 0.1)",
              transition: "background-color 0.3s ease",
            }),
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }),

        muiEditTextFieldProps: {
          name: "name", // Ensure the correct field is targeted
          required: true,
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Block digits 0-9
            if (/^[0-9]$/.test(e.key)) {
              e.preventDefault();
            }
          },
          // error: !!validationErrors?.name,
          // helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors((prev) => ({
              ...prev,
              name: undefined,
            })),
        },
      },
    ],
    [validationErrors]
  );

  //call CREATE hook
  const { mutateAsync: createUser, isPending: isCreatingUser } =
    useCreateUser();
  //call READ hook
  const {
    data,
    isError: isLoadingUsersError,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useGetData(filterModes, pagination, setTotalCount, sorting);
  //call UPDATE hook

  const { mutateAsync: updateUser, isPending: isUpdatingUser } =
    useUpdateUser();
  //call DELETE hook
  const { mutateAsync: deleteUser, isPending: isDeletingUser } =
    useDeleteUser();

  // CREATE FUNCTION TO ADD NEW A BROKER
  const createBroker = async (broker: { id: string; name: string }) => {
    if (!broker.name) {
      setValidationErrors((prev) => ({
        ...prev,
        name: "Broker Name is Required",
      }));
      toast.error("Broker name is required!");
      return;
    }
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    if (!nameRegex.test(broker.name)) {
      toast.error("Broker name can only contain letters, spaces, and hyphens");
      return;
    }

    try {
      await axiosInstance.post(`${backendbaseUrl}/paper-app/brokers/`, broker);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Broker added successfully!");
    } catch (error: any) {
      console.error("Error adding broker:", error);

      // Try to get the most useful message from backend response
      let backendMessage = "Failed to add broker. Please try again.";

      if (error.response?.data) {
        const data = error.response.data;

        // If backend sends object like { fieldName: ["message"] }
        if (typeof data === "object" && !Array.isArray(data)) {
          const messages = Object.values(data)
            .flat() // flatten arrays
            .filter((msg) => typeof msg === "string");
          if (messages.length > 0) {
            backendMessage = messages.join("\n");
          }
        }

        // If backend sends string message
        if (typeof data === "string") {
          backendMessage = data;
        }
      }

      toast.error(backendMessage);
    }
  };

  const formCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!brokerName.trim()) {
      setValidationErrors((prev) => ({ ...prev, name: "Broker name is required" }));
      return;
    }
    if (brokerName.trim().length > 250) {
      setValidationErrors((prev) => ({ ...prev, name: "Broker name cannot exceed 250 characters" }));
      return;
    }

    setValidationErrors((prev) => ({ ...prev, name: undefined }));

    const broker = {
      id: Date.now().toString(),
      name: brokerName.trim(),
    };

    await createBroker(broker);

    // Only reset the form if no validation errors
    if (!validationErrors.name) {
      setBrokerName("");
    }
  };

  //CREATE action
  const handleCreateUser: MRT_TableOptions<User>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    // Validate the input values
    const newValidationErrors = validateUser(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    setValidationErrors({});

    const broker = {
      id: Date.now().toString(), // Generate a unique ID
      name: values.name.trim(), // Use the name from the table values
    };

    await createBroker(broker);

    // Exit creating mode
    table.setCreatingRow(null);
  };

  //UPDATE action
  const handleeditsave: MRT_TableOptions<User>["onEditingRowSave"] = async ({
    values,
    table,
    row, // Add row parameter to access full row data
  }) => {
    // Log the detailed values and table object
    console.log("Saving User - Full Row Original Data:", row.original);
    console.log("Saving User - Edited Values:", values);

    // Merge the edited values with the original row data
    const updatedUser = {
      ...row.original, // Spread the original row data first
      ...values, // Overlay the edited values on top
    };

    console.log("Saving User - Merged User Data:", updatedUser);

    // Validate user data
    const newValidationErrors = validateUser(updatedUser);

    if (!updatedUser.name || updatedUser.name.trim() === "") {
      setValidationErrors((prev) => ({
        ...prev,
        name: "Broker Name is Required",
      }));
      toast.error("Broker Name is Required");
      return;
    }

    // Check for validation errors
    if (Object.values(newValidationErrors).some((error) => error)) {
      console.log("Validation Errors:", newValidationErrors);
      setValidationErrors(newValidationErrors);
      return;
    }

    // Clear previous validation errors
    setValidationErrors({});

    // Trigger user update mutation
    try {
      await updateUser(updatedUser); // Pass the full, merged user object
      toast.success("Broker updated successfully!");
      // Exit editing mode
      table.setEditingRow(null);
    } catch (error: any) {
      console.error("User Update Error:", error);

      let errorMessage = "Failed to update broker. Please try again.";

      // ✅ If backend sends a Python dict-style string
      if (typeof error?.response?.data?.error === "string") {
        const match = error.response.data.error.match(
          /'Broker with this name already exists.'/
        );
        if (match) {
          errorMessage = "Broker with this name already exists.";
        }
      }

      toast.error(errorMessage);
    }
  };

  //DELETE action
  const openDeleteConfirmModal = (row: MRT_Row<User>) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this broker?",
      content: `Deleting ... `,
      onOk: () => {
        deleteUser(row.original.id)
          .then(() => {
            toast.success("Broker deleted Successfully!");
          })
          .catch((error) => {
            toast.error("Failed to delete Broker. Please try again.");
          });
      },
    });
  };

  const handleCopyRow = (row: any) => {
    if (!row) return;
    const rowData = row.original; // Get the row data
    let textToCopy = JSON.stringify(rowData, null, 2); // Convert to formatted JSON
     if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(textToCopy).catch((err) => {
      console.error("Clipboard write failed:", err);
    });
  } else {
    const textarea = document.createElement("textarea");
    textarea.value = textToCopy;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }// Copy to clipboard

    let jsonData;

    try {
      jsonData = JSON.parse(textToCopy);
      setBrokerName(jsonData.name);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("copied and pasted successfully");
      setInputBackgroundColour("#83bf6e26");
      setTimeout(() => {
        setInputBackgroundColour(""); // Then set it again
      }, 2400);
    } catch (error) {
      console.error("Invalid JSON. Pasted as a string:", textToCopy);
      jsonData = { rawText: textToCopy }; // Fallback if not valid JSON
    }
  };

  interface ContextMenuPosition {
    mouseX: number;
    mouseY: number;
  }

  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(
    null
  );

  // Function to handle right-click and open the menu
  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };

  // Function to handle menu close
  const handleClose = () => {
    setContextMenu(null);
  };

  // Function to handle Paste action
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      let jsonData;

      try {
        jsonData = JSON.parse(text);
        setBrokerName(jsonData.name);
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.success("copied and pasted successfully");
        setInputBackgroundColour("#83bf6e26");
        setTimeout(() => {
          setInputBackgroundColour(""); // Then set it again
        }, 2400);
      } catch (error) {
        console.error("Invalid JSON. Pasted as a string:", text);
        jsonData = { rawText: text }; // Fallback if not valid JSON
      }

      // Here, you can use jsonData further as needed
    } catch (err) {
      console.error("Failed to read clipboard contents:", err);
    }

    handleClose();
  };

  // Function to handle Mark action
  const handleResizing = () => {
    setShowRowResizing(!showRowResizing);
    handleClose();
  };

  const handleExportRows = async (rows: MRT_Row<any>[]) => {
    if (rows.length === 0) {
      toast.warn("Please select at least one row to export");
      return;
    }

    try {
      const rowIds = rows.map((row) => row.original.id);
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/export-selected/broker/`,
        { ids: rowIds },
        {
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "selected_brokers.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Export started successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export selected rows");
    }
  };

  const handleDataDownload = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/export-delete/broker`,
        {
          responseType: "blob",
          headers: {
            // Add any required headers
            "Content-Type": "application/json",
          },
        }
      );

      // Check if response is valid
      if (!response.data) {
        throw new Error("No data received from server");
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "all_brokers.csv");
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success("All Data Downloaded Successfully");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download all data");
    }
  };

  const handleBulkDelete = async (rows: MRT_Row<any>[]) => {
    // 1. Get selected row IDs
    const rowIds = rows.map((row) => row.original.id);

    if (rowIds.length === 0) {
      toast.warn("Please select at least one broker to delete.");
      return;
    }

    // 2. Debug: Verify selected IDs before sending
    console.log("Selected IDs for deletion:", rowIds);

    try {
      // 3. Show confirmation dialog
      showDeleteConfirmModal({
        title: `Delete ${rowIds.length} selected broker(s)?`,
        content: `This will permanently delete ${rowIds.length} broker(s). Continue?`,
        onOk: async () => {
          const loadingToast = toast.loading(
            `Deleting ${rowIds.length} broker(s)...`
          );
          const payload = {
            ids: rowIds.filter((id) => id), // Ensure no empty/null IDs
          };
          console.log(payload);
          try {
            // 4. Prepare exact payload format API expects
            const payload = {
              ids: rowIds.filter((id) => id), // Ensure no empty/null IDs
            };

            // 5. Debug: Verify final payload
            console.log("Sending delete payload:", payload);

            // 6. Make API call
            const response = await axiosInstance.delete(
              `${backendbaseUrl}/paper-app/export-delete/broker/`,
              {
                data: payload,
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
              }
            );

            // 7. Handle response
            if (response.status === 200) {
              toast.success(`Successfully deleted ${rowIds.length} broker(s)`);

              // 8. Refresh data and clear selection
              queryClient.invalidateQueries({ queryKey: ["users"] });
              table.setRowSelection({});
            } else {
              throw new Error(
                response.data?.message || "Unknown error occurred"
              );
            }
          } catch (error: any) {
            // 9. Enhanced error handling
            console.error("Delete error details:", {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
            });

            const errorMessage =
              error.response?.data?.detail ||
              error.response?.data?.message ||
              error.message ||
              "Failed to delete brokers";

            toast.error(`Deletion failed: ${errorMessage}`);
          } finally {
            toast.dismiss(loadingToast);
          }
        },
      });
    } catch (error) {
      console.error("Confirmation dialog error:", error);
      toast.error("An error occurred during the deletion process.");
    }
  };
  const table = useMaterialReactTable({
    columns,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    // enableRowNumbers: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onIsFullScreenChange: setIsFullScreen,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    manualSorting: true,
    manualPagination: true,
    enableRowSelection: true,
    // positionToolbarAlertBanner: "bottom",
    // layoutMode: "semantic",
    // enableColumnResizing: showRowResizing,
    enableColumnResizing: true, // Already set
    // columnResizeMode: "onEnd", // Ensures precise resizing
    // defaultColumn: {
    //   minSize: 50, // Smallest practical width
    //   enableResizing: true,
    // },
    enableFilterMatchHighlighting: true,
    rowCount: totalCount,
    manualFiltering: true,
    data: data?.results || [],
    enableDensityToggle: true,
    createDisplayMode: "row", // ('modal', and 'custom' are also available)
    editDisplayMode: "row", // ('modal', 'cell', 'table', and 'custom' are also available)
    enableEditing: true,

    // Top toolbar customization
    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        // Optional: Style the inner MuiBox-root (css-wsew38) for visual confirmation
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),

    muiTableContainerProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),

    muiTableBodyRowProps: ({ row, table }) => ({
      onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();

          const editingRow = table.getState().editingRow;
          if (editingRow) {
            const saveHandler = table.options.onEditingRowSave;
            if (saveHandler) {
              const newedit = {
                ...editingRow.original,
                ...editingRow._valuesCache,
              };
              console.log("Saving:", newedit);

              saveHandler({
                exitEditingMode: () => table.setEditingRow(null),
                row: editingRow,
                table,
                values: newedit,
              });
            }
          }
        }
      },
    }),

    enableColumnOrdering: true, // ✅ Enables column drag-and-drop
    enableRowOrdering: false, // ✅ Enables row drag-and-drop
    getRowId: (row) => row.id,
    enableColumnFilterModes: true, //enable changing filter mode for all columns unless explicitly disabled in a column def
    initialState: { showColumnFilters: true }, //show filters by default
    filterFns: {
      customFilterFn: (row, id, filterValue) => {
        return row.getValue(id) === filterValue;
      },
    },

    // defaultDisplayColumn: {
    //   enableResizing: true, //turn on some features that are usually off for all display columns
    // },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        enableColumnActions: true,
        size: 140, //set custom width
        muiTableHeadCellProps: {
          // align: "center", //change head cell props
        },
      },
      "mrt-row-numbers": {
        enableColumnDragging: true,
        enableColumnOrdering: true, //turn on some features that are usually off
        // enableResizing: true,
        muiTableHeadCellProps: {
          sx: {
            fontSize: "1.2rem",
          },
        },
      },
      "mrt-row-select": {
        enableColumnActions: true,
        enableHiding: true,
        // size: 50,
      },
    },

    renderTopToolbarCustomActions: ({ table }) => {
      const hasSelection = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();

      const buttonsMap: Record<string, React.ReactNode> = {
        "upload": (
          <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
            Upload File
          </Button>
        ),
        "export_all": (
          <Button
            onClick={() => handleDataDownload()}
            startIcon={<BrowserUpdatedIcon />}
          >
            Export All Data
          </Button>
        ),
        "history": (
          <Button
            onClick={() => handlehistory()}
            startIcon={<HistoryIcon />}
          >
            History Log
          </Button>
        ),
        "template": (
          <BrokerTemplateDownloadButton
            apiEndpoint="/paper-app/download-broker-template/"
            buttonText="Download Template"
            fileName="broker_template.csv"
          />
        ),
        "export_selected": (
          <Button
            disabled={!hasSelection}
            onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
            startIcon={<FileDownloadIcon />}
          >
            Export Selected Rows
          </Button>
        ),
        // "export_all_rows": (
        //   <Button
        //     disabled={table.getPrePaginationRowModel().rows.length === 0}
        //     startIcon={<FileDownloadIcon />}
        //   >
        //     Export All Rows
        //   </Button>
        // ),
        // "export_page_rows": (
        //   <Button
        //     disabled={table.getRowModel().rows.length === 0}
        //     startIcon={<FileDownloadIcon />}
        //   >
        //     Export Page Rows
        //   </Button>
        // ),
        "delete_selected": hasSelection ? (
          <Button
            style={{ color: "red" }}
            onClick={() => handleBulkDelete(table.getSelectedRowModel().rows)}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        ) : null,
        "reset": (
          <ResetTablePreferencesButton onOk={() => resetTableState()} />
        ),
      };

      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Box
            className="table-download__wrapper"
            sx={{
              display: "flex",
              gap: "6px",
              padding: "0px",
              flexWrap: "wrap",
            }}
          >
            <SortableContext
              items={currentActionButtonOrder}
              strategy={horizontalListSortingStrategy}
            >
              {currentActionButtonOrder.map((id) => (
                buttonsMap[id] && buttonsConfig[id] && actionButtonVisibility[id] !== false ? (
                  <SortableActionButton key={id} id={id} tooltipText={buttonsConfig[id].tooltipText}>
                    {buttonsMap[id]}
                  </SortableActionButton>
                ) : null
              ))}
            </SortableContext>
          </Box>
        </DndContext>
      );
    },

    renderToolbarInternalActions: ({ table }) => {
      const hasSelection = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();
      const inactiveIds = hasSelection ? [] : ["delete_selected"];

      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TableActionVisibilityMenu
            actionButtonOrder={currentActionButtonOrder}
            setActionButtonOrder={setActionButtonOrder}
            actionButtonVisibility={actionButtonVisibility}
            setActionButtonVisibility={setActionButtonVisibility}
            buttonsConfig={buttonsConfig}
            defaultOrder={defaultButtonOrder}
            inactiveIds={inactiveIds}
          />
          <MRT_ToggleFiltersButton table={table} />
          <TableColumnVisibilityMenu  table={table} />
          <MRT_ToggleDensePaddingButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
        </Box>
      );
    },

    // muiTableProps: {
    //   sx: {
    //     border: '1px solid rgba(81, 81, 81, .5)',
    //     caption: {
    //       captionSide: 'top',
    //     },
    //   },
    // },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        fontStyle: "normal",
        fontWeight: "normal",
      },
    },

    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0",
        border: "1",
      },
    },
    localization: {
      filterCustomFilterFn: "Custom Filter Fn",
      toggleDensity: `Toggle Density (${density})`,
    } as any,

    muiToolbarAlertBannerProps: isLoadingUsersError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,

    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateUser,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleeditsave,

    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "rem" }}>
        <Tooltip title="Edit" className="editIcon">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" className="deleteIcon">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Copy Row">
          <IconButton onClick={() => handleCopyRow(row)}>
            {" "}
            {/* ✅ Pass row as argument */}
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="History" className="History">
          <IconButton color="error" onClick={() => openrowhistory(row)}>
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    state: {
      columnFilters,
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
      density,
      globalFilter,
      grouping,
      pagination,
      sorting,
      showColumnFilters,
      showGlobalFilter,
      isFullScreen,
      isLoading: isLoadingUsers,
      isSaving: isCreatingUser || isUpdatingUser || isDeletingUser,
      showAlertBanner: isLoadingUsersError,
      showProgressBars: isFetchingUsers,
    },
  });

  useEffect(() => {
    const transformedFilters = (columnFilters as MRT_ColumnFiltersState).map((filter) => ({
      ...filter,
      mode: (table.getColumn(filter.id)?.getFilterFn() as any)?.name || "contains",
    }));

    setFilterModes(transformedFilters);
  }, [columnFilters, table]);

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Add New Broker
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="form_contain">
                      <div
                        className={`flex formWrapper ${
                          inputBackgroundColour !== "" ? "inputboxcustom" : ""
                        }`}
                        style={{ backgroundColor: inputBackgroundColour }}
                      >
                        <Box
                          sx={{
                            width: "100%",

                            p: { xs: 1, sm: 1 },
                            pt: "20px !important",
                            backgroundColor: "#ffff",
                            // backgroundColor: "#f8f9fa",

                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            component="form" // This is now the only form element
                            onSubmit={formCreate}
                            className="form_default"
                            //   component="form"
                            //   onSubmit={handleSubmit}
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              justifyContent: "start",
                              gap: 0.5,
                              "& > *": {
                                // flex: "1 1 0px",
                                minWidth: {
                                  xs: "100%",
                                  sm: "48%",
                                  md: "23%",
                                  lg: "15%",
                                  xl: "7.5%",
                                },
                              },
                            }}
                          >
                            <CompactTextField
                              label="Enter Broker"
                              value={brokerName}
                              type="text"
                              onChange={(value) => {
                                const sanitized = value.replace(/[^a-zA-Z0-9\s\-']/g, "");
                                setBrokerName(sanitized);
                              }} name="brokerName"
                              error={Boolean(validationErrors?.name)}
                              helperText={validationErrors?.name || ""}

                              validateOnBlur={(value) => {
                                if (!validateRequired(value)) {
                                  setValidationErrors((prev) => ({
                                    ...prev,
                                    name: "Broker Name is Required",
                                  }));
                                  return null;
                                } else {
                                  setValidationErrors((prev) => ({
                                    ...prev,
                                    name: undefined,
                                  }));
                                  return null;
                                }
                              }}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                                htmlInput: {
                                  endAdornment: brokerName && (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => setBrokerName("")}
                                        edge="end"
                                      >
                                        <ClearIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              }}
                              sx={{
                                flex: { xs: "1 1 auto", sm: "1 1 70%" },
                                "& .MuiInputBase-root": {
                                  fontSize: "0.7rem",
                                  height: { xs: "36px", sm: "32px" },
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.7rem",
                                  transform: "translate(14px, 8px) scale(1)",
                                  "&.MuiInputLabel-shrink": {
                                    transform:
                                      "translate(14px, -6px) scale(0.75)",
                                  },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#C0C0C0",
                                },
                                maxWidth: { sm: "250px", xs: "100%" },
                              }}
                            />
                            <Button
                              variant="contained"
                              type="submit"
                              sx={{
                                height: { xs: "36px", sm: "32px" },
                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                minWidth: { xs: "100%", sm: "100px" },
                                maxWidth: { sm: "150px" },
                                backgroundColor: "#00bcd4",
                                textTransform: "none",
                                fontSize: "0.75rem",
                                "&:hover": {
                                  backgroundColor: "#008c9e",
                                },
                              }}
                            >
                              Add
                            </Button>
                          </Box>
                        </Box>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              <div
                className="table-ad__wrapper"
                onContextMenu={handleContextMenu}
              >
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />

                  {/* Context Menu */}
                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                    }
                  >
                    {/* <MenuItem onClick={handlePaste}>Paste</MenuItem> */}
                    <MenuItem onClick={handleResizing}>
                      {showRowResizing ? "Disable Resizing" : "enable Resizing"}
                    </MenuItem>
                  </Menu>
                </div>
                <FileUpload
                  open={openFileUpload}
                  onFileUploadClose={handleFileUploadClose}
                  onFileUploadSubmit={handleFileUploadSubmit}
                  acceptedFileTypes=".csv"
                />
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

//CREATE hook (post new user to api)
function useCreateUser() {
  const queryClient = useQueryClient();
  console.log("create user");
  return useMutation({
    mutationFn: async (user: User) => {
      //send api update request here
      await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
      return Promise.resolve();
    },
    //client side optimistic update
    onMutate: (newUserInfo: User) => {
      queryClient.setQueryData(
        ["users"],
        (prevUsers: any) =>
          [
            ...prevUsers,
            {
              ...newUserInfo,
              id: (Math.random() + 1).toString(36).substring(7),
            },
          ] as User[]
      );
    },
    // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
  });
}

//READ hook (get users from api)
function useGetData(
  filterModes: { mode: string; id: string; value: unknown }[],
  pagination: { pageIndex: number; pageSize: number },
  setTotalCount: any,
  sorting: any
) {
  return useQuery<{ results: User[]; totalCount: number }>({
    // ✅ Correct return type
    queryKey: ["users", filterModes, pagination, sorting], // Ensure proper re-fetching
    queryFn: async () => {
      let url = `${backendbaseUrl}/paper-app/brokers/?page=${
        pagination.pageIndex + 1
      }&page_size=${pagination.pageSize}`;

      if (filterModes.length > 0) {
        console.log("filterModes---,", filterModes);
        const queryParams = filterModes
          .map(
            (e) =>
              `fieldName__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${
                e.id
              }=${String(e.value)}__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${
                e.mode
              }`
          )
          .join("&");

        url += `&${queryParams}`;
      }
      console.log("sortparams:", sorting);
      if (sorting.length > 0) {
        const sortParams = `sortField_${sorting[0].id}=${sorting[0].desc}_sorting`;
        url += `&${sortParams}`;

        console.log("yesss");
      } else {
        console.log("sortParams");
      }

      const response = await axiosInstance.get(url);
      setTotalCount(response.data.count);
      return {
        results: response.data.results as User[],
        totalCount: response.data.count as number,
      };
    },
    refetchOnWindowFocus: false,
  });
}

function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: User) => {
      // Comprehensive logging
      console.group("🔄 User Update Mutation");
      console.log("Input User Object:", user);

      // Validate basic requirements
      if (!user || !user.id) {
        console.error("❌ Invalid user object:", user);
        throw new Error("User object is invalid or missing ID");
      }

      // Prepare payload - now using the full user object
      const payload = user;

      console.log("📦 Prepared Payload:", payload);

      console.groupEnd();
      try {
        const { data } = await axiosInstance.put(
          `/paper-app/brokers/${payload.id}/`,
          user
        );
        console.log("✅ API Response:", data);
        console.groupEnd();
        return data;
      } catch (error) {
        console.error("❌ API Request Failed:", error);
        throw error;
      }
    },

    // Rest of the mutation remains the same
    onMutate: async (newUserInfo: User) => {
      console.log("🔮 Optimistic Update - New User Info:", newUserInfo);

      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ["users"] });

      // Snapshot previous data
      const previousUsers = queryClient.getQueryData(["users"]);

      // Optimistically update cache
      queryClient.setQueryData(["users"], (oldData: any) =>
        oldData?.map((prevUser: User) =>
          prevUser.id === newUserInfo.id ? newUserInfo : prevUser
        )
      );

      return { previousUsers };
    },

    onError: (err, newUser, context) => {
      console.error("❌ Update Failed:", err);

      // Rollback optimistic update
      queryClient.setQueryData(["users"], context?.previousUsers);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

//DELETE hook (delete user in api)
function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      //send api update request here
      // await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
      try {
        await axiosInstance.delete(
          `${backendbaseUrl}/paper-app/brokers/${userId}/`
        );
        console.log("userId", userId);
      } catch (error) {
        console.error("Error deleting user:", error);
        throw error;
      }

      return Promise.resolve();
    },
    //client side optimistic update
    onMutate: async (userId: string) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });

      const previousUsers = queryClient.getQueryData(["users"]);

      queryClient.setQueryData(["users"], (prevUsers: any) =>
        prevUsers?.filter((user: User) => user.id !== userId)
      );

      return { previousUsers };
    },
    onError: (err, userId, context) => {
      console.error("❌ Delete Failed:", err);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["users"] }), //refetch users after mutation, disabled for demo
  });
}

//react query setup in App.tsx
const ReactQueryDevtoolsProduction = lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
);

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
      {/* <Suspense fallback={null}>
        <ReactQueryDevtoolsProduction />
      </Suspense> */}
    </QueryClientProvider>
  );
}

const validateRequired = (value: string) => !!value.length;
const validateEmail = (email: string) =>
  !!email.length &&
  email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

function validateUser(user: User) {
  return {
    // brokerName: !validateRequired(user.name)
    //   ? "First Name is Required"
    //   : "",
    // name: !validateRequired(user.name) ? "Last Name is Required" : "",
    // email: !validateEmail(user.email) ? "Incorrect Email Format" : "",
  };
}